import { motion } from "framer-motion";
import { ExternalLink } from "lucide-react";
import { useEffect, useState } from "react";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { useLanguage } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const FALLBACK_MEMBERS = [
  {
    name: "Sofi",
    color: "#FF005A",
    cz: "Jsem malá, to mi vcelku vadí, ale narodila jsem se tak, tak je to v pohodě. Myslím, že umím hezky zpívat a že jsem taková crazy. Prostě mě baví dělat blbosti.",
    en: "I am small, which kind of bothers me, but I was born that way so it is fine. I think I can sing well and I am quite crazy. I just love having fun.",
    photo_url: "",
  },
  {
    name: "Agi",
    color: "#00E5FF",
    cz: "Já jsem prostě blondýna, a to má někdy pěkný nevýhody. Ale hlavně dělám, co mě baví, věnuju se tomu naplno a neodsouvám nic na pozdější dobu.",
    en: "I am a blonde, which sometimes has its disadvantages. But mainly I do what I love, I give it my all and I never put anything off.",
    photo_url: "",
  },
  {
    name: "Leyla",
    color: "#E5FF00",
    cz: "Hrozně ráda tancuju. Asi už od chvíle, co jsem se narodila. Je z Egypta a já jsem napůl Egypťanka a napůl Češka. Chodím na zpěv, v tom se chci pořád zlepšovat.",
    en: "I love dancing so much. Probably since the moment I was born. My dad is from Egypt, so I am half Egyptian and half Czech. I take singing lessons and want to keep improving.",
    photo_url: "",
  },
  {
    name: "Naty",
    color: "#FF005A",
    cz: "Občas jsem bláznivá, mívám totiž různé potrefené nápady. Taky jsem kamarádská a ráda pomůžu, když to někdo potřebuje. Tancovala jsem už odmalička.",
    en: "Sometimes I am a little crazy, I have all kinds of wild ideas. I am also friendly and I love to help when someone needs it. I have been dancing since I was little.",
    photo_url: "",
  },
];

const resolvePhoto = (u) => (u ? (u.startsWith("http") ? u : `${BACKEND}${u}`) : "");

export default function AboutPage() {
  const { t, lang } = useLanguage();
  const [content, setContent] = useState(null);
  useEffect(() => {
    axios.get(`${API}/content`).then((r) => setContent(r.data)).catch(() => {});
  }, []);

  // Merge backend members with fallback descriptions when admin only set photos
  const cmsMembers = content?.about_members || [];
  const members = FALLBACK_MEMBERS.map((fb) => {
    const fromCms = cmsMembers.find(
      (m) => (m.name || "").toLowerCase() === fb.name.toLowerCase(),
    );
    return {
      ...fb,
      color: fromCms?.color || fb.color,
      cz: fromCms?.desc_cz || fb.cz,
      en: fromCms?.desc_en || fb.en,
      photo_url: fromCms?.photo_url || fb.photo_url,
    };
  });

  return (
    <div style={{ background: "#050505", minHeight: "100vh" }}>
      <SEO
        title="O kapele – Sofi, Agi, Leyla a Naty"
        description="Poznej Rebelky – českou dívčí kapelu, kterou tvoří Sofi, Agi, Leyla a Naty. Vznikly v září 2023 na konkurzu v karlínském divadle. Pop hudba s českými texty pro mladou generaci."
        keywords="Rebelky kapela, Sofi Rebelky, Agi Rebelky, Leyla Rebelky, Naty Rebelky, dívčí kapela Praha, Pinkofein, kapela pro mládež"
        url="/o-nas"
      />
      <Navbar />
      <div style={{ paddingTop: "5rem" }}>
        {/* Hero */}
        <section style={{ padding: "5rem 1.5rem", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", inset: 0, backgroundImage: "url(https://www.kapelarebelky.cz/wp-content/uploads/2024/06/rebelky-promo-scaled.webp)", backgroundSize: "cover", backgroundPosition: "center", opacity: 0.12 }} />
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(5,5,5,0.5), #050505 90%)" }} />
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <p className="section-label mb-4">{t.about.members}</p>
            <h1 style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 900, fontSize: "clamp(2.5rem, 8vw, 6rem)", color: "#FFFFFF", textTransform: "uppercase", letterSpacing: "-0.04em", marginBottom: "1.5rem" }} className="glow-pink">
              {t.about.title.toUpperCase()}
            </h1>
            <p style={{ color: "#A1A1AA", fontSize: "1.1rem", lineHeight: 1.8, maxWidth: "600px", margin: "0 auto 2rem" }}>
              {t.about.story}
            </p>
            <a
              href="https://www.kapelarebelky.cz/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="official-website-link"
              className="btn-outline-rebel rounded-none inline-flex items-center gap-2"
            >
              <ExternalLink size={14} /> kapelarebelky.cz
            </a>
          </div>
        </section>

        {/* Members */}
        <section style={{ padding: "3rem 1.5rem 5rem" }}>
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {members.map((member, i) => (
                <motion.div
                  key={member.name}
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.15 }}
                  data-testid={`member-${member.name.toLowerCase()}`}
                  className="glass-card rounded-2xl overflow-hidden"
                  style={{ display: "flex", flexDirection: "row", gap: 0, border: `1px solid ${member.color}30`, alignItems: "center", padding: "1rem" }}
                >
                  {/* Circular member photo with neon ring */}
                  <div
                    style={{
                      width: "150px",
                      minWidth: "150px",
                      height: "150px",
                      position: "relative",
                      borderRadius: "50%",
                      overflow: "hidden",
                      background: "#050505",
                      boxShadow: `0 0 30px ${member.color}55, 0 0 0 3px ${member.color}`,
                      marginLeft: "0.5rem",
                    }}
                  >
                    {member.photo_url ? (
                      <img
                        src={resolvePhoto(member.photo_url)}
                        alt={member.name}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        loading="lazy"
                      />
                    ) : (
                      <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: `${member.color}10` }}>
                        <span style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 900, fontSize: "3rem", color: member.color }}>
                          {member.name[0]}
                        </span>
                      </div>
                    )}
                  </div>
                  {/* Content */}
                  <div style={{ padding: "1rem 1.5rem", flex: 1 }}>
                    <div className="flex items-center gap-3 mb-3">
                      <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: member.color, flexShrink: 0, boxShadow: `0 0 10px ${member.color}` }} />
                      <h2 style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 900, fontSize: "1.5rem", color: member.color, textTransform: "uppercase", letterSpacing: "-0.02em" }}>
                        {member.name}
                      </h2>
                    </div>
                    <p style={{ color: "#A1A1AA", fontSize: "0.9rem", lineHeight: 1.7, fontFamily: "Manrope, sans-serif" }}>
                      {lang === "en" ? member.en : member.cz}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section style={{ background: "#0A0A0A", padding: "4rem 1.5rem", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <div className="max-w-4xl mx-auto text-center">
            <p className="section-label mb-4">{lang === "cz" ? "Kontaktujte nás" : "Contact us"}</p>
            <h2 style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 800, fontSize: "clamp(1.5rem, 4vw, 2.5rem)", color: "#FFFFFF", textTransform: "uppercase", marginBottom: "2rem" }}>
              {lang === "cz" ? "Booking & Kontakt" : "Booking & Contact"}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
              <div className="glass-card rounded-xl p-6">
                <p style={{ fontFamily: "Unbounded, sans-serif", fontSize: "0.6rem", color: "#FF005A", letterSpacing: "0.2em", marginBottom: "1rem" }}>BOOKING</p>
                <p style={{ color: "#FFFFFF", fontWeight: 600, marginBottom: "0.3rem" }}>Simona Votyová</p>
                <p style={{ color: "#A1A1AA" }}><a href="mailto:simona@pinkofein.cz" style={{ color: "#A1A1AA" }}>simona@pinkofein.cz</a></p>
                <p style={{ color: "#A1A1AA" }}><a href="tel:+420777557401" style={{ color: "#A1A1AA" }}>+420 777 557 401</a></p>
              </div>
              <div className="glass-card rounded-xl p-6" data-testid="about-press-card">
                <p style={{ fontFamily: "Unbounded, sans-serif", fontSize: "0.6rem", color: "#00E5FF", letterSpacing: "0.2em", marginBottom: "1rem" }}>PRESS</p>
                <p style={{ color: "#FFFFFF", fontWeight: 600, marginBottom: "0.3rem" }}>Monika Soukupová</p>
                <p style={{ color: "#A1A1AA" }}><a href="tel:+420602659014" style={{ color: "#A1A1AA" }}>+420 602 65 90 14</a></p>
                <p style={{ color: "#52525B", fontSize: "0.75rem", marginTop: "0.4rem" }}>
                  {lang === "cz" ? "Mediální zájem, rozhovory, recenze" : "Media inquiries & reviews"}
                </p>
              </div>
              <div className="glass-card rounded-xl p-6">
                <p style={{ fontFamily: "Unbounded, sans-serif", fontSize: "0.6rem", color: "#FF005A", letterSpacing: "0.2em", marginBottom: "1rem" }}>PRODUKCE</p>
                <p style={{ color: "#FFFFFF", fontWeight: 600, marginBottom: "0.3rem" }}>Pinkofein s.r.o.</p>
                <p style={{ color: "#A1A1AA" }}>Komenského 329, Káraný</p>
                <p style={{ color: "#A1A1AA" }}>IČ: 19747888</p>
                <p style={{ color: "#A1A1AA" }}><a href="mailto:produkce@pinkofein.cz" style={{ color: "#A1A1AA" }}>produkce@pinkofein.cz</a></p>
              </div>
            </div>

            {/* Press Kit CTA (veřejný PDF pro produkce, novináře a kluby) */}
            <div className="mt-10" data-testid="investor-pdf-card">
              <a
                href="https://rebelky.online/api/uploads/Rebelky_Press_Kit.pdf"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="investor-pdf-link"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.7rem",
                  padding: "1rem 1.8rem",
                  background: "linear-gradient(135deg, #FF005A 0%, #FF005A 60%, #00E5FF 200%)",
                  color: "#FFFFFF",
                  fontFamily: "Unbounded, sans-serif",
                  fontWeight: 800,
                  fontSize: "0.7rem",
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  textDecoration: "none",
                  borderRadius: "999px",
                  boxShadow: "0 10px 30px rgba(255,0,90,0.3)",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                }}
                onMouseOver={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 14px 36px rgba(255,0,90,0.5)"; }}
                onMouseOut={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 10px 30px rgba(255,0,90,0.3)"; }}
              >
                ★ {lang === "cz" ? "Stáhnout Press Kit pro produkce (PDF)" : "Download Press Kit (PDF)"}
              </a>
              <p style={{ color: "#52525B", fontSize: "0.75rem", marginTop: "0.8rem", fontFamily: "Manrope, sans-serif" }}>
                {lang === "cz"
                  ? "Informace o kapele, koncertech a kontaktní údaje pro promotéry a klubové manažery."
                  : "Band overview, tour dates and contact info for promoters and venue managers."}
              </p>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
}
